import { Button, Tooltip } from 'flowbite-react';
import React, { useEffect, useState } from 'react';
import { HiringAssessmentDocument, MeetingStatus, UIMeeting, UserCareerPathType } from 'types';
import InterviewedBy from '../scorecard/InterviewedBy';
import { canShowJoinCta, canShowRescheduleCta } from 'src/utils/interview';
import MeetingCardScoreComponent from './MeetingCardScoreComponent';
import { useLocation, useNavigate } from 'react-router-dom';
import MeetingRecordingModal from '../scorecard/MeetingRecordingModal';
import { sendTrackingEvent } from 'src/utils/gtm';
import moment from 'moment';
import { useUserContext } from 'src/context/UserProvider';
import { useThemeContext } from 'src/context/ThemeProvider';
import classNames from 'classnames';
import { BiChevronRight } from 'react-icons/bi';
import AssessmentExpired from '../GuestUserAssessment/AssessmentExpired';
import WelcomeAssessment from '../GuestUserAssessment/WelcomeAssessment';

interface MeetingProps {
	meeting?: UIMeeting;
	hiringAssessmentInvite?: HiringAssessmentDocument;
	onReschedule?: () => void;
	isUsedInBanner?: boolean;
}

function isDeadlinePassed(deadline: Date) {
	const deadlineMoment = moment(deadline);
	return deadlineMoment.isBefore(moment());
}

const careerPathDisplayNames: { [key in UserCareerPathType]: string } = {
	[UserCareerPathType.SOFTWARE_DEVELOPMENT]: 'Software Development',
	[UserCareerPathType.DATA_ANALYTICS]: 'Data Analytics',
	[UserCareerPathType.SDET]: 'SDET',
	[UserCareerPathType.TECH_SUPPORT]: 'Tech Support',
	[UserCareerPathType.OPERATIONS]: 'Operations',
	[UserCareerPathType.BD_EXECUTIVE]: 'BD Executive',
	[UserCareerPathType.DEV_OPS]: 'DevOps',
	[UserCareerPathType.MATH_ANNOTATIONS_EXPERT]: 'Math Annotations Expert',
	[UserCareerPathType.SITE_RELIABILITY_ENGINEER]: 'Site Reliability Engineer',
	[UserCareerPathType.SALES]: 'Sales',
	[UserCareerPathType.PRODUCT]: 'Product',
	[UserCareerPathType.HUMAN_RESOURCES]: 'Human Resources',
	[UserCareerPathType.MARKETING]: 'Marketing',
	[UserCareerPathType.HASKELL]: 'Haskell',
	[UserCareerPathType.REACT_NATIVE]: 'React Native',
	[UserCareerPathType.AI_ENGINEER]: 'AI Engineer',
};

const formatPosition = (pos: UserCareerPathType) => {
	return careerPathDisplayNames[pos] || pos;
};

const getUpdatedMeetingTitle = (title: string, careerPath: UserCareerPathType) => {
	const formattedCareerPath = formatPosition(careerPath);
	return title.replace(/Software Developer/, formattedCareerPath);
};

const MeetingCardV2 = ({ meeting, hiringAssessmentInvite, onReschedule, isUsedInBanner = false }: MeetingProps) => {
	const location = useLocation();
	const navigate = useNavigate();
	const { themeMode } = useThemeContext();
	const { user } = useUserContext();
	const showJoinCta = meeting ? canShowJoinCta(meeting.meetingStatus) : false;
	const showRescheduleCta = meeting ? canShowRescheduleCta(meeting, user) : false;

	const [isMeetingRecordingModalOpen, setIsMeetingRecordingModalOpen] = useState<boolean>(false);

	const [maxLength, setMaxLength] = useState<number>(15);
	const [disableJoinCta, setDisableJoinCta] = useState<boolean>(false);

	useEffect(() => {
		if (window.innerWidth < 960) {
			setMaxLength(15);
			setDisableJoinCta(true);
		} else {
			setMaxLength(28);
			setDisableJoinCta(false);
		}
	}, []);

	const handleContinue = (hiringAssessmentId: string) => {
		if (
			isDeadlinePassed(
				(hiringAssessmentInvite?.hiringAssessmentDeadline as Date) ||
				(meeting?.hiringAssessment?.hiringAssessmentDeadline as Date),
			)
		) {
			// window.open(`/placed-hiring-assessment/${hiringAssessmentId}`, '_blank');
			// window.open(`https://test-placed.masaischool.com/jobseeker/placed-hiring-assessment/${hiringAssessmentId}`, '_blank');
			window.open(`https://levelup.masaischool.com/jobseeker/placed-hiring-assessment/${hiringAssessmentId}`, '_blank');
		} else if (hiringAssessmentId) {
			window.open(`https://levelup.masaischool.com/jobseeker/placed-hiring-assessment/${hiringAssessmentId}`, '_blank');
			// window.open(`/placed-hiring-assessment/${hiringAssessmentId}`, '_blank');
		}
	};

	const handleViewRecording = () => {
		setIsMeetingRecordingModalOpen(true);
	};
	const originalTitle = meeting?.title ?? '';
	const careerPath = meeting?.hiringAssessment?.userPreferenceSnapshot?.careerPath as UserCareerPathType;
	const updatedTitle = getUpdatedMeetingTitle(originalTitle, careerPath);
	const careerPathDashboard = meeting?.userPreferenceSnapshot?.careerPath as UserCareerPathType;
	const updatedTitleDashboard = getUpdatedMeetingTitle(originalTitle, careerPathDashboard);
	return (
		<>
			<div className="bg-white universal-card-hover-effect transition-shadow dark:bg-[#1F2A37] rounded-[12px] relative isolate overflow-hidden h-full flex flex-col border dark:border-[#555770]">
				<div>
					<div className="absolute max-w-[230px] right-0 h-full w-full z-[-1] rounded-[8px] [background:_linear-gradient(242.84deg,_rgba(255,_255,_255,_0)_6.73%,_#FFFFFF_42.5%)] dark:[background:_linear-gradient(230.66deg,_rgba(31,_42,_55,_0)_0%,_#1F2A37_40.77%)]"></div>
					<img
						src={`/assets/prep-card-bg-image.svg`}
						alt="challenge"
						className="absolute w-full h-full rounded-[8px] z-[-2] dark:mix-blend-color-burn max-w-[230px] right-0"
					/>
				</div>
				<div className="grow-2  rounded-[12px] dark:bg-[#1F2A37] relative flex flex-col h-full">
					{hiringAssessmentInvite ? (
						<>
							<div className="flex flew-row px-4 w-[102px] bg-gray-50 dark:bg-[#374151] text-[#31C48D] rounded-[6px] gap-[4px]">
								<img src={`/assets/check-list.svg`} alt="check-circle" className="py-[10px] items-center" />
								<span className="flex justify-center text-[#111928] font-inter text-[12px] font-[500] leading-[18px] items-center">
									Invited
								</span>
							</div>
						</>
					) : (
						<div className="flex flew-row px-4 bg-gray-50 dark:bg-[#374151] text-[#31C48D] rounded-br-[6px] gap-[4px] max-w-fit">
							<img src={`/assets/check-list.svg`} alt="check-circle" className="py-[10px] items-center" />
							<span className="flex justify-center text-[#111928] font-inter text-[12px] font-[500] leading-[18px] items-center">
								{meeting?.meetingStatus ?? 'Completed'}
							</span>
						</div>
					)}

					{!meeting?.hiringAssessment ? (
						<div className="dark:text-white text-base font-semibold w-max cursor-pointer px-4">
							{meeting && updatedTitleDashboard.length > maxLength ? (
								<>
									<Tooltip
										onClick={e => {
											e.stopPropagation();
										}}
										content={updatedTitleDashboard}
									>
										<span
											onClick={e => {
												e.stopPropagation();
											}}
										>
											{updatedTitleDashboard.substring(0, maxLength)}...
										</span>
									</Tooltip>
								</>
							) : (
								<>{meeting && updatedTitleDashboard}</>
							)}
						</div>
					) : null}

					{(meeting?.host && meeting?.hiringAssessment) || hiringAssessmentInvite ? (
						<div className="mt-2 px-4">
							<InterviewedBy
								title="Your Interviewer"
								data={{
									interviewerName: meeting?.host?.name ?? '',
									companyName: meeting?.host?.profile?.currentCompany ?? '',
									companyRole: meeting?.host.profile.currentRole ?? '',
									linkedinLink: meeting?.host.profile.linkedInProfile,
									imgUrl: meeting?.host.profile.profileImageUrl ?? '',
								}}
								companydata={{
									meetingTitle: hiringAssessmentInvite ? 'Assessment Test' : updatedTitle,
									companyTitle: hiringAssessmentInvite
										? hiringAssessmentInvite?.company?.name ?? ''
										: meeting?.hiringAssessment?.company?.name ?? '',
									companyURL: hiringAssessmentInvite
										? hiringAssessmentInvite.company?.url
										: meeting?.hiringAssessment?.company?.url,
									companyLogo: hiringAssessmentInvite
										? hiringAssessmentInvite.company?.logo
										: meeting?.hiringAssessment?.company?.logo,
									position: hiringAssessmentInvite
										? hiringAssessmentInvite.userPreferenceSnapshot?.careerPath ?? ''
										: meeting?.hiringAssessment?.userPreferenceSnapshot?.careerPath ?? '',
								}}
								showTrustedText={
									meeting ? [MeetingStatus.SCHEDULED, MeetingStatus.STARTED].includes(meeting.meetingStatus) : false
								}
								isUsedInBanner={true}
							/>
						</div>
					) : null}

					{meeting && meeting.meetingStatus === MeetingStatus.COMPLETED && !meeting?.hiringAssessment ? (
						<div className="text-[12px] text-gray-700 dark:text-[#8F90A6] my-2 px-4">
							{moment(meeting.startTime).format('MMMM Do YYYY, h:mm:ss a')}
						</div>
					) : null}

					{meeting && meeting.meetingStatus === MeetingStatus.COMPLETED ? (
						<div
							className={`flex gap-[14px] mt-auto 
								${isUsedInBanner
									? 'px-4 py-3'
									: `${!meeting.isMock == false ? 'justify-between' : 'justify-end'
									} py-2 px-4 bg-[#FAFAFC] dark:bg-[#111E2C] flex items-center border-t rounded-b-[12px]`
								}
								`}
						>
							<button
								onClick={() => {
									sendTrackingEvent({
										event: 'view_score_card',
										eventLabel: location.pathname,
									});
									window.open(`/scorecard/${meeting._id}`, '_blank');
								}}
								className="flex items-center text-[14px] font-[600] leading-[18px] font-inter text-[#7777E7] dark:text-[#6698FA] p-4"
							>
								View Scorecard
								<BiChevronRight size={'16px'} className="ml-[4px] text-[#7777E7]" />
							</button>
						</div>
					) : showJoinCta || showRescheduleCta || hiringAssessmentInvite ? (
						<div
							className={`flex items-center gap-4 mt-auto ${isUsedInBanner
								? 'px-4 py-3'
								: '-mx-4 px-4 py-3 bg-[#FAFAFC] dark:bg-[#18222D] border-t border-[#E2E8F0] dark:border-gray-600'
								} ${meeting?.meetingStatus === MeetingStatus.PENDING ||
									meeting?.meetingStatus === MeetingStatus.STARTED ||
									hiringAssessmentInvite?.hiringAssessmentDeadline
									? 'justify-between'
									: 'justify-end'
								}`}
						>
							{showRescheduleCta ? (
								<Button
									color={themeMode === 'light' ? 'light' : 'dark'}
									className=" !border !border-solid !border-[#7777E7]"
									onClick={() => {
										isUsedInBanner &&
											sendTrackingEvent({
												event: 'dashboard_reschedule',
												eventLabel: location.pathname,
											});
										onReschedule && onReschedule();
									}}
								>
									Reschedule
								</Button>
							) : null}
							{meeting?.hiringAssessment?.hiringAssessmentDeadline &&
								(meeting.meetingStatus == MeetingStatus.PENDING || meeting?.meetingStatus == MeetingStatus.STARTED) ? (
								<div className="flex gap-2 mt-2">
									<div className="flex items-center gap-[8px] ml-2">
										<img src="/assets/Alarm.svg" alt="assessment" className="w-6 h-6" />
										<div className="flex flex-col">
											<span className="text-[10px] text-[#8F90A6] font-inter font-[500] leading-[14px] break-words">
												Deadline
											</span>
											<span className="text-[12px] text-[#555770] font-inter font-[500] leading-[16px] break-words">
												{moment(meeting?.hiringAssessment?.hiringAssessmentDeadline).format(
													'DD MMM, YYYY [at] hh:mm A',
												)}
											</span>
										</div>
									</div>
								</div>
							) : hiringAssessmentInvite?.hiringAssessmentDeadline ? (
								<div className="flex gap-2 mt-2">
									<div className="flex items-center gap-[8px] ml-2">
										<img src="/assets/Alarm.svg" alt="assessment" className="w-6 h-6" />
										<div className="flex flex-col">
											<span className="text-[10px] text-[#8F90A6] font-inter font-[500] leading-[14px] break-words">
												Deadline
											</span>
											<span className="text-[12px] text-[#555770] font-inter font-[500] leading-[16px] break-words">
												{moment(hiringAssessmentInvite?.hiringAssessmentDeadline).format('DD MMM, YYYY [at] hh:mm A')}
											</span>
										</div>
									</div>
								</div>
							) : (
								<div></div>
							)}

							{showJoinCta ? (
								<Button
									color="purplePlaced"
									className="mr-2"
									onClick={() => {
										isUsedInBanner &&
											sendTrackingEvent({
												event: 'dashboard_join',
												eventLabel: location.pathname,
											});
										handleContinue(meeting?.hiringAssessment?._id);
									}}
									disabled={disableJoinCta}
								>
									Continue
								</Button>
							) : hiringAssessmentInvite ? (
								<Button
									color="purplePlaced"
									className="mr-2"
									onClick={() => {
										isUsedInBanner &&
											sendTrackingEvent({
												event: 'dashboard_start',
												eventLabel: location.pathname,
											});
										handleContinue(hiringAssessmentInvite?._id);
									}}
									disabled={disableJoinCta}
								>
									Start
								</Button>
							) : null}
						</div>
					) : null}
				</div>

				{meeting && [MeetingStatus.SCHEDULED, MeetingStatus.STARTED].includes(meeting.meetingStatus) ? (
					<div
						className={classNames(
							'border-t border-[#E2E8F0] dark:border-gray-600 flex items-center bg-gray-50 dark:bg-[#18222D] font-[400] text-yellow-500 dark:text-[#FDED72B2] md:hidden',
							isUsedInBanner ? 'text-xs px-3 py-1' : 'px-4 py-3 text-[14px] min-h-[67px]',
						)}
					>
						Tech. rounds can be joined only through desktop
					</div>
				) : null}

				{meeting && meeting.meetingStatus === MeetingStatus.JOBSEEKERNOSHOW ? (
					<div className="px-4 py-3 border-t border-[#E2E8F0] dark:border-gray-600 text-[14px] min-h-[67px] flex items-center bg-gray-50 dark:bg-[#18222D] font-[400] text-yellow-500 dark:text-[#FDED72B2]">
						⚠️ You didn't attend the scheduled technical round.
					</div>
				) : null}

				{meeting && [MeetingStatus.RESCHEDULED, MeetingStatus.JOBSEEKERRESCHEDULED].includes(meeting.meetingStatus) ? (
					<div className="px-4 py-3 border-t border-[#E2E8F0] dark:border-gray-600 text-[14px] min-h-[67px] flex items-center bg-gray-50 dark:bg-[#18222D] font-[400] text-yellow-500 dark:text-[#FDED72B2]">
						You have rescheduled this technical round.
					</div>
				) : null}

				{meeting && meeting.meetingStatus === MeetingStatus.MENTORNOSHOW ? (
					<div className="px-4 py-3 border-t border-[#E2E8F0] dark:border-gray-600 text-[14px] min-h-[67px] flex items-center bg-gray-50 dark:bg-[#18222D] font-[400] text-yellow-500 dark:text-[#FDED72B2]">
						⚠️ Interviewer wasn't available
					</div>
				) : null}

				{meeting && meeting.meetingStatus === MeetingStatus.COMPLETED ? (
					<div className="absolute top-4 right-4">
						<MeetingCardScoreComponent score={meeting.score ?? 0} />
					</div>
				) : null}
			</div>

			<MeetingRecordingModal
				isOpen={isMeetingRecordingModalOpen}
				onClose={() => setIsMeetingRecordingModalOpen(false)}
				meetingId={meeting?._id ?? ''}
			/>
		</>
	);
};

export default MeetingCardV2;
